import { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, type PanInfo } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Entry } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { moodOptions, getMoodByText, getEntryIcon } from '@/types/index';
import {
  Search, X, Loader2, ArrowUpDown, ChevronLeft, ChevronRight,
  Calendar as CalendarIcon, List, SlidersHorizontal, Trash2,
} from 'lucide-react';
import {
  format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  subMonths, addMonths, addWeeks, subWeeks, eachDayOfInterval,
  isToday, isSameMonth,
} from 'date-fns';
import { ja } from 'date-fns/locale';
import { getWeekStartsOn, getDayLabels } from '@/lib/utils';

type CardMode = 'compact' | 'standard' | 'rich';
type TagLogic = 'or' | 'and';
type ViewTab = 'list' | 'calendar';
type CalendarSubMode = 'month' | '2week' | '1week';
const PAGE_SIZE = 10;

// Date range presets
const datePresets = [
  { label: '全期間', value: 'all' },
  { label: '今日', value: 'today' },
  { label: '今週', value: 'week' },
  { label: '今月', value: 'month' },
  { label: '3ヶ月', value: '3months' },
  { label: 'カスタム', value: 'custom' },
] as const;

export default function TimelinePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const [allEntries, setAllEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);

  // View tab from URL
  const activeTab: ViewTab = searchParams.get('view') === 'calendar' ? 'calendar' : 'list';
  const setActiveTab = (tab: ViewTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'calendar') {
      next.set('view', 'calendar');
    } else {
      next.delete('view');
    }
    setSearchParams(next, { replace: true });
  };

  // Calendar state
  const [calendarSubMode, setCalendarSubMode] = useState<CalendarSubMode>('month');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());

  // Filters — URL params: ?q=keyword&from=YYYY-MM-DD&to=YYYY-MM-DD&tag=tagname
  const urlQ = searchParams.get('q') || '';
  const urlFrom = searchParams.get('from') || '';
  const urlTo = searchParams.get('to') || '';
  const [searchQuery, setSearchQuery] = useState(urlQ);
  const [searchResults, setSearchResults] = useState<Entry[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [similarityMap, setSimilarityMap] = useState<Record<string, number>>({});
  const [keywordMatchIds, setKeywordMatchIds] = useState<Set<string>>(new Set());
  const [filterMoods, setFilterMoods] = useState<string[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>(() => {
    const tagParam = searchParams.get('tag');
    return tagParam ? [tagParam] : [];
  });
  const [tagLogic, setTagLogic] = useState<TagLogic>('or');
  const [sentimentRange, setSentimentRange] = useState<[number, number]>([-1, 1]);
  const [datePreset, setDatePreset] = useState<string>(() => urlFrom || urlTo ? 'custom' : 'all');
  const [customFrom, setCustomFrom] = useState<Date | undefined>(() => urlFrom ? new Date(urlFrom + 'T00:00:00') : undefined);
  const [customTo, setCustomTo] = useState<Date | undefined>(() => urlTo ? new Date(urlTo + 'T00:00:00') : undefined);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [showFilters, setShowFilters] = useState(() => !!searchParams.get('tag') || !!urlFrom || !!urlTo);

  // Sort & pagination
  const [sortAsc, setSortAsc] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // AI Search setting
  const aiSearchEnabled = localStorage.getItem('aiSearch') !== 'false';

  // Card display mode from settings
  const [cardMode, setCardMode] = useState<CardMode>(
    () => (localStorage.getItem('cardDisplay') as CardMode) || 'standard'
  );

  // Listen for cardDisplay changes
  useEffect(() => {
    const handler = () => {
      setCardMode((localStorage.getItem('cardDisplay') as CardMode) || 'standard');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Keyboard shortcut: Ctrl+N
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        e.preventDefault();
        navigate('/record');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  // Load all entries
  useEffect(() => {
    api.getEntries({ limit: 500 })
      .then(v => setAllEntries(v))
      .catch(err => {
        console.error('[TimelinePage] getEntries failed:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Build dateMap for calendar view: Record<'yyyy-MM-dd', Entry[]>
  const dateMap = useMemo(() => {
    const map: Record<string, Entry[]> = {};
    for (const entry of allEntries) {
      const d = entry.date || entry.created_at?.split('T')[0];
      if (d) {
        if (!map[d]) map[d] = [];
        map[d].push(entry);
      }
    }
    return map;
  }, [allEntries]);

  // Auto-search when navigated with ?q= parameter
  useEffect(() => {
    if (urlQ) {
      setSearching(true);
      api.search(urlQ)
        .then(results => setSearchResults(results))
        .catch(() => setSearchResults([]))
        .finally(() => { setSearching(false); setCurrentPage(1); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Compute date range from preset
  const getDateRange = useCallback((): { from?: string; to?: string } => {
    const today = new Date();
    switch (datePreset) {
      case 'today':
        return { from: format(today, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
      case 'week': {
        const ws = localStorage.getItem('weekStartsOn') === '0' ? 0 : 1;
        const weekStart = startOfWeek(today, { weekStartsOn: ws as 0 | 1 });
        return { from: format(weekStart, 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
      }
      case 'month':
        return { from: format(startOfMonth(today), 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
      case '3months':
        return { from: format(subMonths(today, 3), 'yyyy-MM-dd'), to: format(today, 'yyyy-MM-dd') };
      case 'custom':
        return {
          from: customFrom ? format(customFrom, 'yyyy-MM-dd') : undefined,
          to: customTo ? format(customTo, 'yyyy-MM-dd') : undefined,
        };
      default:
        return {};
    }
  }, [datePreset, customFrom, customTo]);

  // Search handler: AI検索ON → キーワード+セマンティック並列、OFF → キーワードのみ
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSimilarityMap({});
      setKeywordMatchIds(new Set());
      return;
    }
    setSearching(true);
    try {
      if (aiSearchEnabled) {
        const [keywordResult, semanticResult] = await Promise.allSettled([
          api.search(searchQuery),
          api.semanticSearch({ query: searchQuery, limit: 50 }),
        ]);

        const keywordEntries = keywordResult.status === 'fulfilled' ? keywordResult.value : [];
        if (semanticResult.status === 'rejected') {
          console.warn('[search] semantic search failed:', semanticResult.reason);
        }
        const semanticEntries = semanticResult.status === 'fulfilled' ? semanticResult.value.results : [];

        const simMap: Record<string, number> = {};
        semanticEntries.forEach(r => { simMap[r.id] = r.similarity; });

        const kwIds = new Set(keywordEntries.map(e => e.id));
        setKeywordMatchIds(kwIds);

        const semanticIds = new Set(semanticEntries.map(r => r.id));
        const keywordOnly = keywordEntries.filter(e => !semanticIds.has(e.id));

        setSimilarityMap(simMap);
        setSearchResults([...semanticEntries, ...keywordOnly]);
      } else {
        setSimilarityMap({});
        setKeywordMatchIds(new Set());
        setSearchResults(await api.search(searchQuery));
      }
    } catch {
      setSearchResults([]);
      setSimilarityMap({});
      setKeywordMatchIds(new Set());
    } finally {
      setSearching(false);
      setCurrentPage(1);
    }
  }, [searchQuery, aiSearchEnabled]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSimilarityMap({});
    setKeywordMatchIds(new Set());
    setCurrentPage(1);
  };

  // Get unique tags
  const allTags = useMemo(
    () => Array.from(new Set(allEntries.flatMap(e => e.tags || []))),
    [allEntries]
  );

  // Apply all filters
  const filteredEntries = useMemo(() => {
    const source = searchResults ?? allEntries;
    const { from, to } = getDateRange();

    return source.filter(entry => {
      // Mood filter
      if (filterMoods.length > 0) {
        const matched = filterMoods.some(moodId => {
          const mood = moodOptions.find(m => m.id === moodId);
          return mood && entry.mood && (entry.mood.includes(mood.emoji) || entry.mood.includes(mood.label));
        });
        if (!matched) return false;
      }

      // Tag filter
      if (filterTags.length > 0) {
        const entryTags = entry.tags || [];
        if (tagLogic === 'and') {
          if (!filterTags.every(t => entryTags.includes(t))) return false;
        } else {
          if (!filterTags.some(t => entryTags.includes(t))) return false;
        }
      }

      // Sentiment filter
      if (sentimentRange[0] !== -1 || sentimentRange[1] !== 1) {
        const score = entry.sentiment_score;
        if (score === undefined || score === null) return false;
        if (score < sentimentRange[0] || score > sentimentRange[1]) return false;
      }

      // Date range filter
      const entryDate = entry.date || entry.created_at?.split('T')[0];
      if (from && entryDate && entryDate < from) return false;
      if (to && entryDate && entryDate > to) return false;

      return true;
    });
  }, [searchResults, allEntries, filterMoods, filterTags, tagLogic, sentimentRange, getDateRange]);

  // Sort (検索結果はマージ順=類似度順を維持、通常一覧は日付順)
  const sortedEntries = useMemo(() => {
    if (searchResults && Object.keys(similarityMap).length > 0) {
      return filteredEntries; // マージ順（類似度順 + キーワードのみ）を維持
    }
    const sorted = [...filteredEntries].sort((a, b) => {
      const dateA = a.date || a.created_at || '';
      const dateB = b.date || b.created_at || '';
      return sortAsc ? dateA.localeCompare(dateB) : dateB.localeCompare(dateA);
    });
    return sorted;
  }, [filteredEntries, sortAsc, searchResults, similarityMap]);

  // Paginate
  const totalPages = Math.max(1, Math.ceil(sortedEntries.length / PAGE_SIZE));
  const pageEntries = sortedEntries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterMoods, filterTags, tagLogic, sentimentRange, datePreset, customFrom, customTo, sortAsc]);

  const toggleMoodFilter = (moodId: string) => {
    setFilterMoods(prev =>
      prev.includes(moodId) ? prev.filter(m => m !== moodId) : [...prev, moodId]
    );
  };

  const toggleTagFilter = (tag: string) => {
    setFilterTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const hasActiveFilters = filterMoods.length > 0 || filterTags.length > 0 ||
    sentimentRange[0] !== -1 || sentimentRange[1] !== 1 || datePreset !== 'all';

  const clearAllFilters = () => {
    setFilterMoods([]);
    setFilterTags([]);
    setSentimentRange([-1, 1]);
    setDatePreset('all');
    setCustomFrom(undefined);
    setCustomTo(undefined);
  };

  // ─── Delete entry handler ───
  const handleDeleteEntry = useCallback(async (entryId: string) => {
    try {
      await api.deleteEntry(entryId);
      setAllEntries(prev => prev.filter(e => e.id !== entryId));
      if (searchResults) {
        setSearchResults(prev => prev ? prev.filter(e => e.id !== entryId) : null);
      }
      toast({ title: '削除しました' });
    } catch {
      toast({ title: '削除に失敗しました', variant: 'destructive' });
    }
  }, [searchResults, toast]);

  // ─── Calendar navigation helpers ───
  const navigateCalendar = (direction: 'prev' | 'next') => {
    setCalendarDate(prev => {
      if (calendarSubMode === 'month') {
        return direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1);
      }
      if (calendarSubMode === '2week') {
        return direction === 'prev' ? subWeeks(prev, 2) : addWeeks(prev, 2);
      }
      // 1week
      return direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1);
    });
  };

  const ws = getWeekStartsOn();

  const calendarHeaderLabel = useMemo(() => {
    if (calendarSubMode === 'month') {
      return format(calendarDate, 'yyyy年M月', { locale: ja });
    }
    // For week modes, show the range
    const weekStart = startOfWeek(calendarDate, { weekStartsOn: ws });
    const weekEnd = calendarSubMode === '2week'
      ? endOfWeek(addWeeks(weekStart, 1), { weekStartsOn: ws })
      : endOfWeek(weekStart, { weekStartsOn: ws });
    return `${format(weekStart, 'M/d', { locale: ja })} 〜 ${format(weekEnd, 'M/d', { locale: ja })}`;
  }, [calendarDate, calendarSubMode, ws]);

  // Compute days for the calendar grid
  const calendarDays = useMemo(() => {
    if (calendarSubMode === 'month') {
      const monthStart = startOfMonth(calendarDate);
      const monthEnd = endOfMonth(calendarDate);
      const gridStart = startOfWeek(monthStart, { weekStartsOn: ws });
      const gridEnd = endOfWeek(monthEnd, { weekStartsOn: ws });
      return eachDayOfInterval({ start: gridStart, end: gridEnd });
    }
    if (calendarSubMode === '2week') {
      const weekStart = startOfWeek(calendarDate, { weekStartsOn: ws });
      const weekEnd = endOfWeek(addWeeks(weekStart, 1), { weekStartsOn: ws });
      return eachDayOfInterval({ start: weekStart, end: weekEnd });
    }
    // 1week
    const weekStart = startOfWeek(calendarDate, { weekStartsOn: ws });
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: ws });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [calendarDate, calendarSubMode, ws]);

  const handleCalendarDayClick = (day: Date) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const entries = dateMap[dateStr];
    if (entries && entries.length > 0) {
      navigate(`/entry/${entries[0].id}`);
    } else {
      navigate(`/record?date=${dateStr}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">過去のひとりごと</h1>
        {activeTab === 'list' && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSortAsc(!sortAsc)} className="gap-1">
              <ArrowUpDown className="h-4 w-4" />
              {sortAsc ? '古い順' : '新しい順'}
            </Button>
          </div>
        )}
      </div>

      {/* Tab switcher: segment control */}
      <div className="flex gap-0.5 bg-muted rounded-lg p-0.5 w-fit">
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'list' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'
          }`}
          onClick={() => setActiveTab('list')}
        >
          <List className="h-3.5 w-3.5" />
          リスト
        </button>
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            activeTab === 'calendar' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'
          }`}
          onClick={() => setActiveTab('calendar')}
        >
          <CalendarIcon className="h-3.5 w-3.5" />
          カレンダー
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════ */}
      {/* LIST VIEW TAB                                      */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeTab === 'list' && (
        <>
          {/* Search bar - sticky */}
          <div className="sticky top-0 z-30 bg-background pb-2 pt-1 -mx-3 px-3 sm:-mx-4 sm:px-4 lg:-mx-8 lg:px-8 border-b">
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="キーワード・意味で検索"
                  className="pl-10"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.nativeEvent.isComposing && handleSearch()}
                />
                {searchQuery && (
                  <button className="absolute right-3 top-1/2 -translate-y-1/2" onClick={clearSearch}>
                    <X className="h-4 w-4 text-muted-foreground" />
                  </button>
                )}
              </div>
              <Button onClick={handleSearch} disabled={searching} size="icon" variant="outline">
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
              <Button
                variant={showFilters ? 'default' : 'outline'}
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </div>

            {/* Expandable filter section */}
            <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
              <div className="space-y-3 pb-3">
                {/* Mood filter */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">気分</p>
                  <div className="flex flex-wrap gap-1.5">
                    {moodOptions.map(mood => (
                      <Badge
                        key={mood.id}
                        variant={filterMoods.includes(mood.id) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleMoodFilter(mood.id)}
                      >
                        {mood.emoji} {mood.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Tag filter */}
                {allTags.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <p className="text-xs font-medium text-muted-foreground">タグ</p>
                      {filterTags.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-xs"
                          onClick={() => setTagLogic(tagLogic === 'or' ? 'and' : 'or')}
                        >
                          {tagLogic === 'or' ? 'OR' : 'AND'}
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {allTags.slice(0, 20).map(tag => (
                        <Badge
                          key={tag}
                          variant={filterTags.includes(tag) ? 'default' : 'secondary'}
                          className="cursor-pointer"
                          onClick={() => toggleTagFilter(tag)}
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sentiment range */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">
                    感情スコア: {sentimentRange[0].toFixed(1)} 〜 {sentimentRange[1].toFixed(1)}
                  </p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">-1.0</span>
                    <div className="flex-1 relative h-6">
                      {/* Track background */}
                      <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-1.5 rounded-full bg-muted" />
                      {/* Active range highlight */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-primary/40"
                        style={{
                          left: `${((sentimentRange[0] + 1) / 2) * 100}%`,
                          right: `${(1 - (sentimentRange[1] + 1) / 2) * 100}%`,
                        }}
                      />
                      {/* Min thumb */}
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.1}
                        value={sentimentRange[0]}
                        onChange={e => {
                          const v = parseFloat(e.target.value);
                          setSentimentRange([Math.min(v, sentimentRange[1]), sentimentRange[1]]);
                        }}
                        className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                      />
                      {/* Max thumb */}
                      <input
                        type="range"
                        min={-1}
                        max={1}
                        step={0.1}
                        value={sentimentRange[1]}
                        onChange={e => {
                          const v = parseFloat(e.target.value);
                          setSentimentRange([sentimentRange[0], Math.max(v, sentimentRange[0])]);
                        }}
                        className="absolute inset-0 w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">1.0</span>
                  </div>
                </div>

                {/* Date range */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">期間</p>
                  <div className="flex flex-wrap gap-1.5">
                    {datePresets.map(preset => (
                      <Badge
                        key={preset.value}
                        variant={datePreset === preset.value ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => setDatePreset(preset.value)}
                      >
                        {preset.label}
                      </Badge>
                    ))}
                  </div>
                  {datePreset === 'custom' && (
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => setShowFromCalendar(true)}
                      >
                        <CalendarIcon className="h-3 w-3" />
                        {customFrom ? format(customFrom, 'yyyy/M/d') : '開始日'}
                      </Button>
                      <span className="text-xs text-muted-foreground">〜</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => setShowToCalendar(true)}
                      >
                        <CalendarIcon className="h-3 w-3" />
                        {customTo ? format(customTo, 'yyyy/M/d') : '終了日'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Clear filters */}
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} className="gap-1">
                    <X className="h-3 w-3" /> フィルタをクリア
                  </Button>
                )}
              </div>
              </motion.div>
            )}
            </AnimatePresence>
          </div>

          {/* Results info */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {sortedEntries.length}件
              {searchResults !== null ? ' (検索結果)' : ''}
            </span>
            <span>{currentPage} / {totalPages} ページ</span>
          </div>

          {/* Entry list */}
          {pageEntries.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                {searchResults !== null ? '検索結果がありません' : 'エントリがありません'}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {pageEntries.map((entry, i) => (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.04 }}
                  >
                    <SwipeableEntryCard entry={entry} mode={cardMode} onDelete={handleDeleteEntry} similarity={similarityMap[entry.id]} isKeywordMatch={keywordMatchIds.has(entry.id)} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 py-4">
              <Button
                variant="outline"
                size="icon"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage(p => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce<(number | 'ellipsis')[]>((acc, p, i, arr) => {
                  if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('ellipsis');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, i) =>
                  item === 'ellipsis' ? (
                    <span key={`e-${i}`} className="px-2 text-muted-foreground">...</span>
                  ) : (
                    <Button
                      key={item}
                      variant={currentPage === item ? 'default' : 'outline'}
                      size="icon"
                      onClick={() => setCurrentPage(item as number)}
                    >
                      {item}
                    </Button>
                  )
                )}
              <Button
                variant="outline"
                size="icon"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════════════ */}
      {/* CALENDAR VIEW TAB                                  */}
      {/* ═══════════════════════════════════════════════════ */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          {/* Navigation header — minimal with circular buttons */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigateCalendar('prev')}
              className="h-9 w-9 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex flex-col items-center">
              <h2 className="text-lg font-bold tracking-tight">{calendarHeaderLabel}</h2>
              <div className="flex items-center gap-1 mt-1">
                <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
                  {([
                    { key: 'month' as const, label: '月間' },
                    { key: '2week' as const, label: '2週' },
                    { key: '1week' as const, label: '週' },
                  ] as const).map(mode => (
                    <button
                      key={mode.key}
                      onClick={() => setCalendarSubMode(mode.key)}
                      className={`px-2 py-0.5 text-[10px] font-medium rounded-md transition-all ${
                        calendarSubMode === mode.key
                          ? 'bg-background shadow-sm text-foreground'
                          : 'text-muted-foreground'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setCalendarDate(new Date())}
                  className="px-2 py-0.5 text-[10px] font-medium rounded-full text-primary hover:bg-primary/10 transition-colors ml-1"
                >
                  今日
                </button>
              </div>
            </div>
            <button
              onClick={() => navigateCalendar('next')}
              className="h-9 w-9 flex items-center justify-center rounded-full bg-muted hover:bg-muted/80 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Day-of-week header */}
          <div className={`grid grid-cols-7 ${calendarSubMode === 'month' ? 'gap-1 sm:gap-1.5' : 'gap-1.5 sm:gap-2'}`}>
            {getDayLabels(ws).map((label) => (
              <div
                key={label}
                className={`text-center text-[10px] font-semibold uppercase tracking-wider pb-1 ${
                  label === '土' ? 'text-blue-500/70' : label === '日' ? 'text-red-500/70' : 'text-muted-foreground/50'
                }`}
              >
                {label}
              </div>
            ))}
          </div>

          {/* Calendar grid — card-based dashboard style */}
          <div className="bg-muted/30 rounded-2xl p-1.5 sm:p-2">
            {calendarSubMode === 'month' ? (
              <MonthGrid
                days={calendarDays}
                calendarDate={calendarDate}
                dateMap={dateMap}
                onDayClick={handleCalendarDayClick}
              />
            ) : (
              <WeekGrid
                days={calendarDays}
                dateMap={dateMap}
                onDayClick={handleCalendarDayClick}
              />
            )}
          </div>

          {/* Legend — gradient bar with end labels */}
          <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground">
            <span>ネガティブ</span>
            <div className="flex h-2 w-24 rounded-full overflow-hidden">
              <span className="flex-1 bg-red-500" />
              <span className="flex-1 bg-orange-400" />
              <span className="flex-1 bg-yellow-400" />
              <span className="flex-1 bg-lime-400" />
              <span className="flex-1 bg-green-500" />
            </div>
            <span>ポジティブ</span>
          </div>
        </div>
      )}

      {/* Calendar dialogs (for list filter custom date picker) */}
      <Dialog open={showFromCalendar} onOpenChange={setShowFromCalendar}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>開始日を選択</DialogTitle></DialogHeader>
          <Calendar
            selected={customFrom}
            onSelect={d => { setCustomFrom(d); setShowFromCalendar(false); }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={showToCalendar} onOpenChange={setShowToCalendar}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>終了日を選択</DialogTitle></DialogHeader>
          <Calendar
            selected={customTo}
            onSelect={d => { setCustomTo(d); setShowToCalendar(false); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Sentiment color helpers (matches MoodChart: red→orange→yellow→lime→green) ───
function getSentimentColor(score: number | undefined): string {
  if (score == null) return 'bg-muted';
  if (score <= -0.5) return 'bg-red-500';
  if (score <= -0.1) return 'bg-orange-400';
  if (score <= 0.2) return 'bg-yellow-400';
  if (score <= 0.5) return 'bg-lime-400';
  return 'bg-green-500';
}

function getSentimentTint(score: number | undefined): string {
  if (score == null) return '';
  if (score <= -0.5) return 'bg-red-50 dark:bg-red-950/30';
  if (score <= -0.1) return 'bg-orange-50 dark:bg-orange-950/30';
  if (score <= 0.2) return '';
  if (score <= 0.5) return 'bg-lime-50 dark:bg-lime-950/30';
  return 'bg-green-50 dark:bg-green-950/30';
}

// ─── Month Grid Component — Dashboard card style ───
function MonthGrid({
  days,
  calendarDate,
  dateMap,
  onDayClick,
}: {
  days: Date[];
  calendarDate: Date;
  dateMap: Record<string, Entry[]>;
  onDayClick: (day: Date) => void;
}) {
  return (
    <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
      {days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const entries = dateMap[dateStr] || [];
        const isCurrentMonth = isSameMonth(day, calendarDate);
        const today = isToday(day);
        const dayOfWeek = day.getDay();
        const avgSentiment = entries.length > 0
          ? entries.reduce((sum, e) => sum + (e.sentiment_score ?? 0), 0) / entries.length
          : undefined;
        const tint = getSentimentTint(avgSentiment);

        return (
          <button
            key={dateStr}
            onClick={() => onDayClick(day)}
            className={`
              relative flex flex-col items-center gap-0.5 rounded-xl p-1 sm:p-1.5
              min-h-[3.2rem] sm:min-h-[4.2rem] transition-all
              ${isCurrentMonth
                ? `bg-card shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.2)] hover:shadow-md ${tint}`
                : 'opacity-25'
              }
              ${today ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}
            `}
          >
            {/* Day number — small, subdued */}
            <span
              className={`text-[10px] sm:text-[11px] leading-none ${
                !isCurrentMonth
                  ? 'text-muted-foreground/40'
                  : today
                    ? 'text-primary font-bold'
                    : dayOfWeek === 6
                      ? 'text-blue-500 font-medium'
                      : dayOfWeek === 0
                        ? 'text-red-500 font-medium'
                        : 'text-muted-foreground font-medium'
              }`}
            >
              {format(day, 'd')}
            </span>

            {entries.length > 0 ? (
              <div className="flex flex-col items-center gap-0.5">
                {/* Mood emoji — hero element */}
                <span className="text-base sm:text-lg leading-none">{getEntryIcon(entries[0])}</span>
                {/* Sentiment dot + count chip */}
                <div className="flex items-center gap-0.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${getSentimentColor(avgSentiment)}`} />
                  {entries.length > 1 && (
                    <span className="text-[8px] font-semibold text-muted-foreground bg-muted rounded-full px-1">
                      +{entries.length - 1}
                    </span>
                  )}
                </div>
              </div>
            ) : isCurrentMonth ? (
              <span className="text-muted-foreground/15 text-xs mt-0.5">·</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ─── Week Grid Component — Dashboard card style ───
function WeekGrid({
  days,
  dateMap,
  onDayClick,
}: {
  days: Date[];
  dateMap: Record<string, Entry[]>;
  onDayClick: (day: Date) => void;
}) {
  return (
    <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
      {days.map(day => {
        const dateStr = format(day, 'yyyy-MM-dd');
        const entries = dateMap[dateStr] || [];
        const today = isToday(day);
        const dayOfWeek = day.getDay();
        const firstEntry = entries[0];
        const avgSentiment = entries.length > 0
          ? entries.reduce((sum, e) => sum + (e.sentiment_score ?? 0), 0) / entries.length
          : undefined;
        const tint = getSentimentTint(avgSentiment);

        return (
          <button
            key={dateStr}
            onClick={() => onDayClick(day)}
            className={`
              relative flex flex-col items-center rounded-xl p-1.5 sm:p-2
              min-h-[5rem] sm:min-h-[6.5rem] transition-all
              bg-card shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-[0_1px_3px_rgba(0,0,0,0.2)]
              hover:shadow-md ${tint}
              ${today ? 'ring-2 ring-primary ring-offset-1 ring-offset-background' : ''}
            `}
          >
            <span
              className={`text-[10px] sm:text-[11px] leading-none mb-1 ${
                today
                  ? 'text-primary font-bold'
                  : dayOfWeek === 6
                    ? 'text-blue-500 font-medium'
                    : dayOfWeek === 0
                      ? 'text-red-500 font-medium'
                      : 'text-muted-foreground font-medium'
              }`}
            >
              {format(day, 'M/d')}
            </span>
            {firstEntry ? (
              <div className="flex flex-col items-center gap-0.5 w-full min-w-0">
                <span className="text-lg sm:text-xl leading-none">{getEntryIcon(firstEntry)}</span>
                <div className="flex items-center gap-0.5">
                  <span className={`h-1.5 w-1.5 rounded-full ${getSentimentColor(avgSentiment)}`} />
                  {entries.length > 1 && (
                    <span className="text-[8px] font-semibold text-muted-foreground bg-muted rounded-full px-1">
                      +{entries.length - 1}
                    </span>
                  )}
                </div>
                <span className="text-[10px] sm:text-xs text-muted-foreground truncate w-full text-center leading-tight font-medium">
                  {firstEntry.title || 'エントリ'}
                </span>
              </div>
            ) : (
              <span className="text-muted-foreground/15 text-xs mt-1">·</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Swipeable wrapper for entry cards ───
const SWIPE_THRESHOLD = -100;

function SwipeableEntryCard({
  entry,
  mode,
  onDelete,
  similarity,
  isKeywordMatch,
}: {
  entry: Entry;
  mode: CardMode;
  onDelete: (id: string) => void;
  similarity?: number;
  isKeywordMatch?: boolean;
}) {
  const x = useMotionValue(0);
  const bgOpacity = useTransform(x, [-150, -50, 0], [1, 0.6, 0]);
  const iconScale = useTransform(x, [-120, -60, 0], [1, 0.8, 0]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < SWIPE_THRESHOLD) {
      onDelete(entry.id);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-xl">
      {/* Red background revealed on swipe */}
      <motion.div
        style={{ opacity: bgOpacity }}
        className="absolute inset-0 bg-destructive flex items-center justify-end pr-6 rounded-xl"
      >
        <motion.div style={{ scale: iconScale }} className="flex flex-col items-center gap-1 text-destructive-foreground">
          <Trash2 className="h-5 w-5" />
          <span className="text-xs font-medium">削除</span>
        </motion.div>
      </motion.div>

      {/* Draggable card */}
      <motion.div
        style={{ x }}
        drag="x"
        dragConstraints={{ left: -150, right: 0 }}
        dragElastic={{ left: 0.3, right: 0 }}
        onDragEnd={handleDragEnd}
        className="relative z-10"
      >
        <TimelineEntryCard entry={entry} mode={mode} similarity={similarity} isKeywordMatch={isKeywordMatch} />
      </motion.div>
    </div>
  );
}

// ─── Timeline Entry Card with 3 display modes ───
function TimelineEntryCard({ entry, mode, similarity, isKeywordMatch }: { entry: Entry; mode: CardMode; similarity?: number; isKeywordMatch?: boolean }) {
  const mood = entry.mood ? getMoodByText(entry.mood) : undefined;
  const dateStr = entry.date || entry.created_at;
  const formattedDate = dateStr
    ? format(parseISO(dateStr), 'M月d日 (E)', { locale: ja })
    : '';

  // Sentiment color bar
  const sentimentColor = entry.sentiment_score !== undefined
    ? entry.sentiment_score > 0.3 ? 'bg-green-500' : entry.sentiment_score < -0.3 ? 'bg-red-500' : 'bg-yellow-500'
    : '';

  // 生のコサイン類似度 (0.3–0.8) を体感的に自然な表示スコア (50–99%) にマッピング
  const displaySimilarity = similarity != null
    ? Math.min(99, Math.round((50 + 49 * Math.sqrt(Math.max(0, (similarity - 0.3) / 0.5)))))
    : null;

  const matchBadge = isKeywordMatch ? (
    <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full px-2 py-0.5 whitespace-nowrap">
      キーワード一致
    </span>
  ) : displaySimilarity != null ? (
    <span className="text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 whitespace-nowrap">
      {displaySimilarity}%
    </span>
  ) : null;

  if (mode === 'compact') {
    return (
      <Link to={`/entry/${entry.id}`}>
        <Card className="rounded-xl hover:shadow-md transition-all hover:border-primary/30 cursor-pointer">
          <CardContent className="p-3 flex items-center gap-3">
            <span className="text-xl">{mood?.emoji || '📝'}</span>
            <span className="font-medium text-sm truncate flex-1">{entry.title || 'エントリ'}</span>
            {matchBadge}
            <span className="text-xs text-muted-foreground whitespace-nowrap">{formattedDate}</span>
          </CardContent>
        </Card>
      </Link>
    );
  }

  if (mode === 'rich') {
    return (
      <Link to={`/entry/${entry.id}`}>
        <Card className="rounded-xl hover:shadow-md transition-all hover:border-primary/30 cursor-pointer">
          {sentimentColor && <div className={`h-1 ${sentimentColor}`} />}
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl flex-shrink-0 mt-0.5">{mood?.emoji || '📝'}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="font-medium text-sm truncate">{entry.title || 'エントリ'}</h3>
                  <div className="flex items-center gap-2">
                    {matchBadge}
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{formattedDate}</span>
                  </div>
                </div>
                {entry.summary && (
                  <p className="text-sm text-muted-foreground line-clamp-1 mb-1">{entry.summary}</p>
                )}
                <p className="text-sm text-muted-foreground/70 line-clamp-2">
                  {entry.content?.replace(/^---[\s\S]*?---\s*/, '').replace(/^#\s+.*\n+>\s+.*\n+##\s+Transcript\s*\n/, '').trim().slice(0, 200)}
                </p>
                {entry.tags && entry.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {entry.tags.slice(0, 6).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Standard mode (default)
  return (
    <Link to={`/entry/${entry.id}`}>
      <Card className="hover:shadow-md transition-all hover:border-primary/30 cursor-pointer overflow-hidden">
        {sentimentColor && <div className={`h-1 ${sentimentColor}`} />}
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl flex-shrink-0 mt-0.5">{mood?.emoji || '📝'}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="font-medium text-sm truncate">{entry.title || 'エントリ'}</h3>
                <div className="flex items-center gap-2">
                  {matchBadge}
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formattedDate}</span>
                </div>
              </div>
              {entry.summary && (
                <p className="text-sm text-muted-foreground line-clamp-1">{entry.summary}</p>
              )}
              {entry.tags && entry.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {entry.tags.slice(0, 4).map(tag => (
                    <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                  ))}
                  {entry.tags.length > 4 && (
                    <Badge variant="outline" className="text-xs">+{entry.tags.length - 4}</Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
